import { createFileRoute } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Historia } from '../features/historia/components/historia';

/**
 * `/saber-mas` (T-3.10, oleada 3, `specs/diseno-2026-09-02/`; fusionada con `/instalar` el
 * 2026-09-15 — ver
 * `docs/decisions/ADR-0023-fusion-de-saber-mas-e-instalar.md`): ruta abierta, sin guarda — «cómo
 * hemos llegado hasta aquí» es para quien todavía no está dentro. `/instalar` ya no existe como
 * ruta propia: su contenido (`features/instalar/components/instalar.tsx`) vive incrustado dentro
 * de esta pantalla, alcanzable por el ancla `/saber-mas#instalar` — el componente de presentación
 * (`features/historia/components/historia.tsx`) es quien monta `<Instalar />` bajo un contenedor
 * con `id='instalar'`. Ese componente no conoce rutas: es esta ruta quien decide los dos destinos
 * del cierre.
 *
 * - `verFuncionamiento` → `/bienvenida`: con sesión, esa ruta enseña el recorrido directamente.
 *   Sin sesión, no hace falta que esta pantalla lo compruebe por su cuenta — el propio layout de
 *   `/bienvenida` (`routes/_autenticado.tsx`) ya redirige a `/login?redirect=/bienvenida` en su
 *   `beforeLoad` antes de renderizar nada, así que "pasa primero por `/login`" ya sale gratis del
 *   guard que protege esa ruta.
 * - `crearCuenta` → `/login`: acceso directo, sin pasar por `/bienvenida`.
 *
 * Marco `mx-auto max-w-3xl`: el `<main>` de `routes/__root.tsx` ya aplica ese mismo ancho, pero la
 * tarea de esta oleada lo pide también aquí en la propia ruta.
 */
export const Route = createFileRoute('/saber-mas')({ component: PantallaSaberMas });

function PantallaSaberMas(): React.JSX.Element {
	const { t } = useTranslation('puerta');

	// Título de pestaña del navegador: no hay ningún gestor de `<title>` instalado todavía (ni
	// `HeadContent` de TanStack Router montado en `main.tsx`, fichero congelado de esta oleada, ni
	// una librería nueva — prohibida). Se restaura el título anterior al desmontar, para no dejar
	// "Cómo hemos llegado hasta aquí" puesto si se navega a otra ruta que no gestiona el suyo propio.
	// Trasladado desde `routes/instalar.tsx` (borrada el 2026-09-15, ver ADR-0023): era el único
	// `document.title` reactivo al idioma de toda la SPA, y perderlo sería una regresión silenciosa.
	useEffect(() => {
		const tituloAnterior = document.title;
		document.title = t('historia.titulo');
		return () => {
			document.title = tituloAnterior;
		};
	}, [t]);

	return (
		<div className='mx-auto max-w-3xl'>
			<Historia destinoCrearCuenta='/login' destinoVerFuncionamiento='/bienvenida' />
		</div>
	);
}
